/**
 * floor-calendar-data — the data adapter behind the Floor Calendar screen
 * (calendar insertion phase 2; decisions in docs/calendar-data-type-map.md,
 * source map in docs/floor-calendar-port.md, claim c-2026-0808-01g).
 *
 * ONE hook assembles the day board the prototype rendered from its inline
 * TENANTS seed — but every field here arrives through the serving path:
 *   staff columns + shifts ........ getOpsBoard / getScheduleWeek (wf-api)
 *   appointments (full detail) .... listAppointments (appointments-api)
 *   events ........................ ops board events (StudioEventDto-backed)
 *   queues + live entries ......... ops board queues
 *   manager calendar blocks ....... calendar-day blocks client
 *   external Outlook/Google busy .. ABSENT until REQ-CAL-2's listExternalBusy
 *                                   lands (seed doc external-busy.json is
 *                                   ready); the board renders nothing rather
 *                                   than inventing busy time.
 * No fixture data lives in this file — the no-fake-interactivity mandate and
 * the serving-path rule both apply in full.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Appointment, listAppointments, listAppointmentServices,
  rescheduleAppointment, cancelAppointment, checkInAppointment, markAppointmentNoShow,
} from "../appointments/appointments-api";
import { getOpsBoard, OpsBoardSnapshot, listEventStaffing, assignEventRole, getScheduleWeek, WfShift } from "../workforce/wf-api";
import { apiRaw, listEventGuests, listInbox, listResources, type StudioResourcesRequestOptions } from "@v0y4ge/api-client";
import { listQueues } from "../queues/queues-api";
import { fetchEventsList } from "../../lib/api";
import { calendarKeyStartIso, toParts } from "../../lib/datetime";
/* calendar-day rides the barrel as a NAMESPACE export (`export * as calendarDay`)
 * — the preview resolver has no package-subpath mapping for a deep import. */
import { calendarDay } from "@v0y4ge/api-client";
const { listCalendarDayBlocks, createCalendarDayBlock, deleteCalendarDayBlock } = calendarDay;

/** Grid window, minutes-of-day — same span the prototype board proved. */
export const DAY_START = 8 * 60;
export const DAY_END = 19 * 60;

export const hmToMin = (hm: string): number => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hm || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};
export const minToHm = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

/** Civil-date authority helpers. Calendar dates are labels in an IANA zone,
 * never browser-local instants. */
export function isValidIanaZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: zone }).format(); return true; }
  catch { return false; }
}

export function civilTodayInZone(now: Date = new Date(), zone: string): string {
  if (!isValidIanaZone(zone)) throw new RangeError("Invalid calendar time zone: " + zone);
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const get = (kind: string) => parts.find((p) => p.type === kind)?.value || "";
  return get("year") + "-" + get("month") + "-" + get("day");
}

export function addCivilDays(iso: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m || !Number.isInteger(days)) throw new RangeError("Invalid civil date");
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole civil days from `fromISO` to `toISO` (UTC arithmetic, DST-safe). */
export function daysBetweenCivil(fromISO: string, toISO: string): number {
  const p = (iso: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) throw new RangeError("Invalid civil date");
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  };
  return Math.round((p(toISO) - p(fromISO)) / 86400000);
}

export interface FloorStaffCol {
  staffId: string;
  name: string;
  role: string;
  /** live floor status when the ops board reports one */
  status?: string | null;
}
export interface FloorApptRow {
  appt: Appointment;
  staffId: string | null;
  staffName: string | null;
  fromMin: number;
  toMin: number;
}
export interface FloorEventRow {
  id: string;
  /** the verified canonical StudioEventDto row (capacity/description/category live here) */
  studio?: Record<string, unknown> | null;
  /** StudioEventDto id (sw_*) — the id Event Day actually reads — taken ONLY
   * from the one canonical row verified as this event on this board's location
   * and civil day (see linkBoardEvents). Null otherwise; the rail then renders
   * WITHOUT the jump-off rather than deep-linking into the wrong event. */
  studioId: string | null;
  title: string;
  fromMin: number;
  toMin: number;
  registered: number;
  checkedIn: number;
  staffOnShift: number;
  staffNeeded: number;
  status: string;
  /** The room the event occupies, when the served row names one (REQ-CAL-4). */
  resource: string | null;
}
export interface FloorQueueRow {
  id: string;
  /** The Concierge family's own queue id (its `?q=` param) — joined by NAME;
   * the ops board's synthetic id means nothing to that screen. */
  consoleId: string | null;
  name: string;
  waiting: number;
  avgWaitMins: number;
  longestWaitMins: number;
  staffed: number;
  needed: number;
  entries: { id: string; name: string; joinedAt: string; service?: string | null; status: string }[];
}
/** Queue-duty rota block on a staff column (served: /v1/admin/floor/queue-duty). */
export interface FloorDutyRow {
  id: string;
  staffName: string;
  queueName: string;
  fromMin: number;
  toMin: number;
}
/** Event-coverage assignment on a staff column (workforce event-staffing family). */
export interface FloorCoverageRow {
  id: string;
  eventId: string;
  roleId: string;
  roleLabel: string;
  eventTitle: string;
  staffId: string;
  staffName: string;
  fromMin: number;
  toMin: number;
}
export interface FloorRoomCol {
  id: string;
  name: string;
}
/** A staff member's shift window + breaks for the day (workforce schedules). */
export interface FloorShiftRow {
  staffId: string | null;
  staffName: string;
  fromMin: number;
  toMin: number;
  breaks: { fromMin: number; toMin: number; kind: string }[];
}
/** Service name → list price, for the derived revenue tile. */
export type FloorPriceMap = Record<string, number>;
export interface FloorBlockRow {
  id: string;
  label: string;
  staffId?: string | null;
  /** external-busy rows join by name (no staff ids in that family) */
  staffName?: string;
  fromMin: number;
  toMin: number;
  /** calendar-day block kind as served; the screen treats unknown kinds as busy */
  kind?: string | null;
}
export interface FloorDay {
  /** The civil date this read was REQUESTED for. While a newer date loads, the
   * hook keeps returning the previous day, so callers compare this against the
   * date they asked for (isFloorDayFor) before posting or writing. */
  dateISO: string;
  /** The location the read was requested for (null = the session's default). */
  requestedLocationId: string | null;
  /** The request scope this read was issued under, echoed verbatim from
   * `opts.scope` (see floorRequestScope); null for a caller that passes none.
   * Optional so existing FloorDay builders stay valid: absent reads as null. */
  requestScope?: string | null;
  tz: string | null;
  locationId: string | null;
  locationName: string | null;
  staff: FloorStaffCol[];
  appointments: FloorApptRow[];
  events: FloorEventRow[];
  queues: FloorQueueRow[];
  blocks: FloorBlockRow[];
  duty: FloorDutyRow[];
  coverage: FloorCoverageRow[];
  rooms: FloorRoomCol[];
  shifts: FloorShiftRow[];
  prices: FloorPriceMap;
  /** event guest rows in the prototype's shape: [name, tier, status] */
  guests: [string, string, string][];
  /** inbox conversations in the prototype's thread shape (msgs load lazily) */
  threads: { id: string; name: string; chan: string; when: string; unread: boolean; preview: string; context: string; msgs: unknown[] }[];
  /** REQ-CAL-2 seam: stays empty (never invented) until listExternalBusy serves. */
  externalBusy: FloorBlockRow[];
  /** Whether the canonical event join ran to completion for this board. When
   * false, no event carries a studioId and no guests were read; `error` says why
   * (no board location/zone authority, or an incomplete canonical read). */
  eventLinking: { ok: boolean; error: string | null };
  /** which reads answered — drives the DataSourceStrip */
  sources: { op: string; ok: boolean; error?: string }[];
}

const apptRow = (a: Appointment): FloorApptRow => ({
  appt: a,
  staffId: a.staffId,
  staffName: a.staffName,
  fromMin: hmToMin(a.start),
  toMin: hmToMin(a.end),
});

/** The ops-board floor roster and the appointments family use DIFFERENT staff id
 * schemes (wf-layer ids vs seed-world rows that carry only the staff NAME with
 * staffId null) — the durable shared key is the person's name. Normalized for
 * the join; ids still win when both sides carry one. */
export const staffKey = (name: string | null | undefined): string => (name || "").trim().toLowerCase();

/** ISO time → minutes-of-day in the board's wall clock (server sends wall-clock
 *  HH:MM for ops rows; ISO datetimes fall back to their local clock face). */
const anyToMin = (v: string): number => {
  if (/^\d{1,2}:\d{2}/.test(v)) return hmToMin(v);
  const d = new Date(v);
  return isNaN(d.getTime()) ? 0 : d.getHours() * 60 + d.getMinutes();
};

/* ── Event linking: the board's location, civil day and clock ─────────────
 * An ops-board event carries a title and wall-clock times; the id Event Day
 * reads (sw_*) lives only on the canonical Studio events row, and WorkForce ops
 * ids are not Studio ids. So the jump-off exists only when ONE canonical row is
 * verifiably the same event on THIS board:
 *   - the ops board snapshot is the authority for the location and the IANA
 *     zone, even when the caller named no location. Without both there is no
 *     canonical read and no link; the browser zone and the event's own timezone
 *     never stand in for them;
 *   - the canonical read asks for the board's civil day as two real instants
 *     (start of that day and start of the next, in the board zone) plus the
 *     board's locationId, and walks every page before deciding absence or
 *     uniqueness. A failed page or inconsistent page metadata fails closed:
 *     no links, no guests;
 *   - the worker compares stored strings inclusively, so rows are filtered
 *     again here: same location_id, and an explicit start instant (Z or offset)
 *     in [day start, next day start). Offsetless timestamps are rejected rather
 *     than read as browser-local time;
 *   - a link needs the same title AND the same start minute on the board clock,
 *     matched by exactly one canonical row (and exactly one ops row). A lone
 *     same-titled row at another time is a different event, not a fallback. */
type StudioRow = Record<string, unknown>;

const INSTANT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|([+-])(\d{2}):(\d{2}))$/i;

/** Epoch ms of an ISO timestamp ONLY when it names an instant (a Z or numeric
 * offset). Parsed by hand: an offsetless string never reaches the browser's
 * local-time interpretation, and sub-millisecond digits are truncated rather
 * than left to engine-specific parsing. Null otherwise. */
export function explicitInstantMs(v: unknown): number | null {
  if (typeof v !== "string") return null;
  const m = INSTANT_RE.exec(v.trim());
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const h = Number(m[4]), mi = Number(m[5]), s = Number(m[6] || "0");
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) return null;
  const ms = m[7] ? Number((m[7] + "00").slice(0, 3)) : 0;
  const wall = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  const chk = new Date(wall);
  if (chk.getUTCFullYear() !== y || chk.getUTCMonth() !== mo - 1 || chk.getUTCDate() !== d) return null;
  if (m[8].toUpperCase() === "Z") return wall;
  const oh = Number(m[10]), om = Number(m[11]);
  if (oh > 23 || om > 59) return null;
  const off = (oh * 60 + om) * 60000;
  return m[9] === "+" ? wall - off : wall + off;
}

/** The board's civil day as real instants, under the board's own authority. */
export interface BoardDayWindow {
  dateISO: string;
  tz: string;
  locationId: string;
  /** start of `dateISO` in `tz`, Z-suffixed ISO */
  fromIso: string;
  /** start of the NEXT civil day in `tz`, Z-suffixed ISO (exclusive bound) */
  toIso: string;
  fromMs: number;
  toMs: number;
}

/** Null unless the board supplied a location id AND a valid IANA zone, and both
 * day boundaries resolve to explicit instants on the right civil dates. */
export function boardDayWindow(dateISO: string, tz: string | null | undefined, locationId: string | null | undefined): BoardDayWindow | null {
  if (typeof locationId !== "string" || !locationId.trim() || !isValidIanaZone(tz)) return null;
  let next: string;
  try { next = addCivilDays(dateISO, 1); } catch { return null; }
  const fromIso = calendarKeyStartIso(dateISO, tz);
  const toIso = calendarKeyStartIso(next, tz);
  const fromMs = explicitInstantMs(fromIso);
  const toMs = explicitInstantMs(toIso);
  if (fromMs == null || toMs == null || toMs <= fromMs) return null;
  if (civilTodayInZone(new Date(fromMs), tz) !== dateISO || civilTodayInZone(new Date(toMs), tz) !== next) return null;
  return { dateISO, tz, locationId, fromIso, toIso, fromMs, toMs };
}

/** A canonical row's start as an explicit instant, else null. */
const canonicalStartMs = (row: StudioRow): number | null => {
  for (const k of ["startsAt", "start_utc", "start_at"]) {
    const ms = explicitInstantMs(row[k]);
    if (ms != null) return ms;
  }
  return null;
};

const minuteOnBoard = (ms: number, win: BoardDayWindow): number | null => {
  const p = toParts(new Date(ms), win.tz);
  return p ? p.h * 60 + p.mi : null;
};

export interface BoardDayStudioRow { row: StudioRow; id: string; titleKey: string; startMin: number }

/** Canonical rows that belong to THIS board: the board's location id, and an
 * explicit start instant inside the board's civil day (half-open). */
export function boardDayStudioRows(rows: StudioRow[], win: BoardDayWindow): BoardDayStudioRow[] {
  const out: BoardDayStudioRow[] = [];
  for (const row of rows) {
    if (typeof row.location_id !== "string" || row.location_id !== win.locationId) continue;
    if (typeof row.id !== "string" || !row.id) continue;
    const titleKey = staffKey(typeof row.title === "string" ? row.title : "");
    if (!titleKey) continue;
    const at = canonicalStartMs(row);
    if (at == null || at < win.fromMs || at >= win.toMs) continue;
    const startMin = minuteOnBoard(at, win);
    if (startMin == null) continue;
    out.push({ row, id: row.id, titleKey, startMin });
  }
  return out;
}

/** An ops event's start minute on the board clock: wall-clock HH:MM as the ops
 * board serves it, or an explicit instant inside the board day. Null otherwise. */
export function opsStartMinute(start: string | null | undefined, win: BoardDayWindow): number | null {
  const s = (start || "").trim();
  const hm = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s);
  if (hm) {
    const h = Number(hm[1]), mi = Number(hm[2]);
    return h < 24 && mi < 60 ? h * 60 + mi : null;
  }
  const at = explicitInstantMs(s);
  if (at == null || at < win.fromMs || at >= win.toMs) return null;
  return minuteOnBoard(at, win);
}

/** For each ops event (same order), the ONE canonical row that is that event on
 * this board, or null. Title + start minute must match exactly one board-day
 * canonical row, and no other ops event may claim the same title + minute. */
export function linkBoardEvents(opsEvents: { title: string; start: string }[], dayRows: BoardDayStudioRow[], win: BoardDayWindow): (StudioRow | null)[] {
  const keys = opsEvents.map((e) => {
    const titleKey = staffKey(e.title);
    const startMin = opsStartMinute(e.start, win);
    return titleKey && startMin != null ? { titleKey, startMin, k: titleKey + "@" + startMin } : null;
  });
  const opsCount = new Map<string, number>();
  keys.forEach((x) => { if (x) opsCount.set(x.k, (opsCount.get(x.k) || 0) + 1); });
  return keys.map((x) => {
    if (!x || opsCount.get(x.k) !== 1) return null;
    const hits = dayRows.filter((r) => r.titleKey === x.titleKey && r.startMin === x.startMin);
    return hits.length === 1 ? hits[0].row : null;
  });
}

/** Thrown when a newer load supersedes a read in flight. */
class SupersededFloorRead extends Error {}

const STUDIO_PAGE_LIMIT = 50;
const STUDIO_MAX_PAGES = 40;
const isCount = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n) && n >= 0;

/** Every canonical row the events worker returns for this board's day window
 * and location, across ALL pages (page/limit; no cursor). Throws, rather than
 * returning a partial set, on a failed page, inconsistent pagination metadata,
 * a total that moves mid-read, a duplicate or missing id, or a short read. */
export async function readBoardDayStudioEvents(win: BoardDayWindow, isCurrent: () => boolean = () => true): Promise<StudioRow[]> {
  const rows: StudioRow[] = [];
  const ids = new Set<string>();
  let total: number | null = null;
  for (let page = 1; page <= STUDIO_MAX_PAGES; page++) {
    const res = (await fetchEventsList({ page, limit: STUDIO_PAGE_LIMIT, from: win.fromIso, to: win.toIso, locationId: win.locationId })) as unknown;
    if (!isCurrent()) throw new SupersededFloorRead("superseded");
    const r = (res && typeof res === "object" ? res : {}) as Record<string, unknown>;
    const pg = (r.pagination && typeof r.pagination === "object" ? r.pagination : null) as Record<string, unknown> | null;
    const data = r.data;
    if (!Array.isArray(data) || !pg) throw new Error(`events page ${page}: missing data or pagination`);
    if (!isCount(r.total) || r.total !== pg.total || typeof r.hasMore !== "boolean" || r.hasMore !== pg.hasMore)
      throw new Error(`events page ${page}: total/hasMore disagree with pagination`);
    if (pg.page !== page || pg.limit !== STUDIO_PAGE_LIMIT || !isCount(pg.total_pages))
      throw new Error(`events page ${page}: pagination answered page ${String(pg.page)} limit ${String(pg.limit)}`);
    if (total != null && r.total !== total) throw new Error(`events page ${page}: total changed during the read`);
    total = r.total;
    if (r.events !== undefined) {
      const mirror = r.events;
      if (!Array.isArray(mirror) || mirror.length !== data.length
        || mirror.some((m, i) => (m as StudioRow | null)?.id !== (data[i] as StudioRow | null)?.id))
        throw new Error(`events page ${page}: events and data disagree`);
    }
    if (data.length > STUDIO_PAGE_LIMIT) throw new Error(`events page ${page}: more rows than the page limit`);
    for (const row of data) {
      const id = row && typeof row === "object" ? (row as StudioRow).id : null;
      if (typeof id !== "string" || !id || ids.has(id)) throw new Error(`events page ${page}: missing or repeated event id`);
      ids.add(id);
      rows.push(row as StudioRow);
    }
    if (rows.length > total) throw new Error(`events: ${rows.length} rows exceed total ${total}`);
    if (!r.hasMore) {
      if (rows.length !== total) throw new Error(`events: read ${rows.length} of ${total} rows`);
      return rows;
    }
    if (data.length === 0) throw new Error(`events page ${page}: hasMore on an empty page`);
  }
  throw new Error(`events: more than ${STUDIO_MAX_PAGES} pages for one board day`);
}

/* ── Rooms: the canonical catalog resource list ───────────────────────────
 * The board's rooms come from the typed listResources op (resources.read,
 * /v1/admin/catalog/resources). The raw /v1/admin/resources-editor path this
 * used to call is mocked but mounted by no worker, so a real tenant never
 * answered it. The read stays tenant-wide (no kind/status/location filter), as
 * the board has always used it, and only id + name reach FloorRoomCol. */
type CatalogResourcePage = Awaited<ReturnType<typeof listResources>>;

/* Same options shape the Resources screen passes (useResources.ts); the typed
 * client prefers getConfiguredApiBaseUrl() over this base. `surface` only names
 * this screen in the x-studio-surface header — it grants nothing. */
const FLOOR_RESOURCES_OPTS: StudioResourcesRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "floor",
};
const RESOURCE_PAGE_LIMIT = 200;
const RESOURCE_MAX_PAGES = 50;
const isSafeCount = (n: unknown): n is number => typeof n === "number" && Number.isSafeInteger(n) && n >= 0;

/** Every catalog resource across ALL pages (page/limit at the contract's max
 * limit). Throws, rather than returning a partial list, on a failed page, a page
 * or limit the server did not answer for, a total that moves mid-read, a
 * missing or repeated id, an empty or short page that claims more, or a final
 * count that disagrees with the total. */
export async function readFloorRooms(isCurrent: () => boolean = () => true): Promise<FloorRoomCol[]> {
  const rooms: FloorRoomCol[] = [];
  const ids = new Set<string>();
  let total: number | null = null;
  for (let page = 1; page <= RESOURCE_MAX_PAGES; page++) {
    if (!isCurrent()) throw new SupersededFloorRead("superseded");
    const res: CatalogResourcePage | null | undefined = await listResources({ page: String(page), limit: String(RESOURCE_PAGE_LIMIT) }, FLOOR_RESOURCES_OPTS);
    if (!isCurrent()) throw new SupersededFloorRead("superseded");
    const pg = res && typeof res === "object" ? res.pagination : null;
    const data = res && typeof res === "object" ? res.data : null;
    if (!Array.isArray(data) || !pg || typeof pg !== "object") throw new Error(`resources page ${page}: missing data or pagination`);
    if (pg.page !== page || pg.limit !== RESOURCE_PAGE_LIMIT || !isSafeCount(pg.total) || typeof pg.hasMore !== "boolean")
      throw new Error(`resources page ${page}: pagination answered page ${String(pg.page)} limit ${String(pg.limit)} total ${String(pg.total)}`);
    if (total != null && pg.total !== total) throw new Error(`resources page ${page}: total changed during the read`);
    total = pg.total;
    if (data.length > RESOURCE_PAGE_LIMIT) throw new Error(`resources page ${page}: more rows than the page limit`);
    for (const r of data) {
      const id = r && typeof r === "object" ? r.id : null;
      if (typeof id !== "string" || !id || ids.has(id)) throw new Error(`resources page ${page}: missing or repeated resource id`);
      if (typeof r.name !== "string") throw new Error(`resources page ${page}: resource ${id} has no name`);
      ids.add(id);
      rooms.push({ id, name: r.name || id });
    }
    if (rooms.length > total) throw new Error(`resources: ${rooms.length} rows exceed total ${total}`);
    if (!pg.hasMore) {
      if (rooms.length !== total) throw new Error(`resources: read ${rooms.length} of ${total} rows`);
      return rooms;
    }
    if (data.length === 0) throw new Error(`resources page ${page}: hasMore on an empty page`);
    if (data.length !== RESOURCE_PAGE_LIMIT || rooms.length >= total)
      throw new Error(`resources page ${page}: hasMore on a short page or past the total`);
  }
  throw new Error(`resources: more than ${RESOURCE_MAX_PAGES} pages`);
}

type EventLinkRead = { ok: true; links: (StudioRow | null)[] } | { ok: false; error: string };

/** Options for useFloorCalendarDay. `enabled: false` (JAI-1935) is a caller
 * with no valid location yet: nothing is requested, a read in flight is
 * superseded (its response is discarded and a resource walk stops at its next
 * page), and the previous query's day is cleared rather than kept. Defaults to
 * true, so a caller that passes no options is unchanged. */
export interface FloorCalendarDayOptions {
  enabled?: boolean;
  /** The request scope (JAI-1935): an opaque key for WHO the read is for — the
   * confirmed tenant, its generation and the canonical location id (build it
   * with floorRequestScope). It is part of the request identity, so a change
   * re-reads and supersedes every read in flight even when the date and
   * location id are unchanged (a tenant generation switch under the same
   * location id). Every FloorDay echoes it as `requestScope`, and a day from
   * another scope is never returned. Default null: no scope, unchanged. */
  scope?: string | null;
}

/** The request scope a location-scoped caller passes as `opts.scope`: the
 * confirmed tenant key, its generation and the canonical location id. Opaque;
 * compare with ===. */
export const floorRequestScope = (tenantKey: string | null, generation: number, locationId: string | null): string =>
  JSON.stringify([tenantKey ?? null, generation, locationId ?? null]);

export function useFloorCalendarDay(dateISO: string, locationId?: string | null, opts: FloorCalendarDayOptions = {}) {
  const enabled = opts.enabled ?? true;
  const scope = opts.scope ?? null;
  const [day, setDay] = useState<FloorDay | null>(null);
  const [loading, setLoading] = useState(enabled);
  /* A failure belongs to the scope it was read under (see `error` below). */
  const [failure, setFailure] = useState<{ scope: string | null; message: string } | null>(null);
  const seq = useRef(0);
  /* The identity of the LATEST render's request, written during render rather
   * than in an effect. A changed scope, date, location or enabled flag — even a
   * tenant generation switch under the same location id and date, batched so
   * that no disabled render ever committed — supersedes every read in flight
   * at once, before this hook's effect runs and whether or not a newer load has
   * started yet. Multi-page walks stop at their next page. */
  const identity = JSON.stringify([enabled, dateISO, locationId ?? null, scope]);
  const identityRef = useRef(identity);
  identityRef.current = identity;

  const load = useCallback(async () => {
    /* A reload captured under an older request is not a back door: it neither
     * reads nor touches the current request's state. */
    if (identity !== identityRef.current) return;
    const my = ++seq.current;
    const current = () => my === seq.current && identity === identityRef.current;
    if (!enabled) {
      /* No request, and no stale board: a day kept from an earlier location or
       * tenant must not survive into a state with no valid location. */
      setDay(null);
      setFailure(null);
      setLoading(false);
      return;
    }
    /* Another scope's day is dropped rather than kept while this one loads; a
     * same-scope refresh (another date, a reload) keeps the day on screen. */
    setDay((d) => (d && (d.requestScope ?? null) !== scope ? null : d));
    setLoading(true);
    setFailure(null);
    const sources: FloorDay["sources"] = [];
    const grab = async <T,>(op: string, p: Promise<T>): Promise<T | null> => {
      try {
        const r = await p;
        sources.push({ op, ok: true });
        return r;
      } catch (e) {
        sources.push({ op, ok: false, error: e instanceof Error ? e.message : String(e) });
        return null;
      }
    };
    const mondayOf = (iso: string) => { const d = new Date(iso + "T12:00:00"); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return d.toISOString().slice(0, 10); };
    /* The ops board answers FIRST for the event join: its location id and zone
     * are the only authority the canonical events read may use. The other
     * families load alongside; only the events read waits on the board. */
    const boardRead = grab("getOpsBoard", getOpsBoard(dateISO, locationId ?? undefined));
    const readEventLinks = async (board: OpsBoardSnapshot | null): Promise<EventLinkRead> => {
      const refuse = (error: string): EventLinkRead => {
        sources.push({ op: "linkStudioEvents", ok: false, error });
        return { ok: false, error };
      };
      if (!board) return refuse("no ops board snapshot, so no board location or time zone to link events against");
      if (locationId && board.locationId !== locationId) return refuse("the ops board answered for a different location than the one requested");
      const win = boardDayWindow(dateISO, board.tz, board.locationId);
      if (!win) return refuse("the ops board did not supply a location id and a valid IANA time zone");
      let rows: StudioRow[];
      try {
        rows = await readBoardDayStudioEvents(win, current);
        sources.push({ op: "fetchEventsList", ok: true });
      } catch (e) {
        if (e instanceof SupersededFloorRead) return { ok: false, error: "superseded" };
        const msg = e instanceof Error ? e.message : String(e);
        sources.push({ op: "fetchEventsList", ok: false, error: msg });
        return { ok: false, error: "canonical events read incomplete: " + msg };
      }
      return { ok: true, links: linkBoardEvents(board.events || [], boardDayStudioRows(rows, win), win) };
    };
    const linkRead = boardRead.then(readEventLinks);
    const [appts, board, blocks, consoleQueues, dutyRes, staffingRes, roomsRes, weekRes, busyRes, svcRes, inboxRes, link] = await Promise.all([
      grab("listAppointments", listAppointments({ date: dateISO, locationId: locationId ?? undefined })),
      boardRead,
      grab("listCalendarDayBlocks", listCalendarDayBlocks({ date: dateISO })),
      /* jump-off id joins: the two target screens read DIFFERENT families than
       * the ops board — the Concierge's listQueues and the events family are
       * the id spaces their deep links understand. */
      grab("listQueues", listQueues(null)),
      grab("listQueueDuty", apiRaw<{ duty: unknown[] }>(`/v1/admin/floor/queue-duty?date=${encodeURIComponent(dateISO)}`, undefined, "listQueueDuty")),
      grab("listEventStaffing", listEventStaffing(dateISO, dateISO, locationId ?? undefined)),
      /* typed catalog read, every page or nothing (readFloorRooms) */
      grab("listResources", readFloorRooms(current)),
      grab("getScheduleWeek", getScheduleWeek(mondayOf(dateISO), locationId ?? undefined)),
      grab("listExternalBusy", apiRaw<{ blocks: unknown[] }>(`/v1/admin/floor/external-busy?date=${encodeURIComponent(dateISO)}`, undefined, "listExternalBusy")),
      grab("listAppointmentServices", listAppointmentServices()),
      grab("listInbox", (listInbox as unknown as (q?: Record<string, unknown>) => Promise<unknown>)({ limit: 12 })),
      linkRead,
    ]);
    if (!current()) return; /* a newer load superseded this one */
    /* guests ride the board's first VERIFIED linked event (the prototype's guest
     * list is global). Never a row picked from the events response by itself:
     * no verified link on this board, no guest read. */
    let guests: [string, string, string][] = [];
    const guestEvent = link.ok ? link.links.find((r): r is StudioRow => r != null) ?? null : null;
    if (guestEvent && typeof guestEvent.id === "string" && guestEvent.id) {
      try {
        const g = await listEventGuests(guestEvent.id, { limit: 30 });
        if (!current()) return;
        sources.push({ op: "listEventGuests", ok: true });
        guests = (((g as { data?: unknown[] }).data || []) as Record<string, unknown>[]).map((r) => [
          String(r.name || r.customer_name || r.guestName || ""),
          String(r.type || r.tier || r.notes || r.email || ""),
          String(r.status || "confirmed").replace("checked_in", "checked-in"),
        ] as [string, string, string]).filter((r) => r[0]);
      } catch (eG) {
        if (!current()) return;
        sources.push({ op: "listEventGuests", ok: false, error: eG instanceof Error ? eG.message : String(eG) });
      }
    }
    if (!current()) return; /* a newer load superseded this one */
    if (!appts && !board) {
      setFailure({ scope, message: sources.map((s) => (!s.ok ? `${s.op}: ${s.error || "failed"}` : null)).filter(Boolean).join(" · ") || "No data" });
      setLoading(false);
      setDay(null);
      return;
    }
    const b: OpsBoardSnapshot | null = board;
    /* Staff columns: the ops-board floor roster is who is actually on today.
     * Appointments referencing staff missing from it still get a column, so a
     * served booking can never silently vanish off the board. */
    const staff: FloorStaffCol[] = (b?.floor || []).map((f) => ({ staffId: f.staffId, name: f.name, role: f.role, status: f.status }));
    (appts?.appointments || []).forEach((a) => {
      const hit = staff.some((s) => (a.staffId && s.staffId === a.staffId) || (a.staffName && staffKey(s.name) === staffKey(a.staffName)));
      if (!hit && (a.staffId || a.staffName))
        staff.push({ staffId: a.staffId || "name:" + staffKey(a.staffName), name: a.staffName || a.staffId || "", role: "", status: null });
    });
    const blockRows: FloorBlockRow[] = ((blocks as any)?.blocks || (blocks as any)?.entries || []).map((x: any) => ({
      id: String(x.id), label: x.label || x.title || "Blocked", staffId: x.staffId ?? null,
      fromMin: anyToMin(x.start || x.from || ""), toMin: anyToMin(x.end || x.to || ""), kind: x.kind ?? null,
    }));
    setDay({
      dateISO,
      requestedLocationId: locationId ?? null,
      requestScope: scope,
      tz: b?.tz ?? null,
      locationId: b?.locationId ?? appts?.locationId ?? null,
      locationName: b?.locationName ?? null,
      staff,
      appointments: (appts?.appointments || []).map(apptRow),
      events: (b?.events || []).map((e, i) => {
        /* studioId only from the canonical row linkBoardEvents verified for this
         * ops event on this board's location and civil day; else no CTA. */
        const raw = link.ok ? link.links[i] ?? null : null;
        return {
          id: e.id, studioId: raw && typeof raw.id === "string" ? raw.id : null, studio: raw,
          title: e.title, fromMin: anyToMin(e.start), toMin: anyToMin(e.end),
          registered: e.registered, checkedIn: e.checkedIn, staffOnShift: e.staffOnShift,
          staffNeeded: e.staffNeeded, status: e.status,
          resource: (e as unknown as { resource?: string | null }).resource ?? null,
        };
      }),
      queues: (b?.queues || []).map((q) => {
        const cq = (Array.isArray((consoleQueues as any)?.data) ? (consoleQueues as any).data : [])
          .find((x: any) => staffKey(x.name) === staffKey(q.name));
        return {
          id: q.id, consoleId: cq ? String(cq.id) : null,
          name: q.name, waiting: q.waiting, avgWaitMins: q.avgWaitMins,
          longestWaitMins: q.longestWaitMins, staffed: q.staffed, needed: q.needed, entries: q.entries || [],
        };
      }),
      blocks: blockRows,
      duty: (((dutyRes as any)?.duty || []) as any[]).map((r) => ({
        id: String(r.id), staffName: String(r.staff || ""), queueName: String(r.queue || ""),
        fromMin: hmToMin(String(r.from || "")), toMin: hmToMin(String(r.to || "")),
      })),
      /* one coverage block per role ASSIGNEE, spanning the role's window.
       * DATE-FILTERED (2026-08-13): the staffing route returns the whole week's
       * plans and the board join below is by TITLE — without this filter a plan
       * for the same event title on ANOTHER day put its people on today's board,
       * double-booking them against their real appointments. */
      coverage: (((staffingRes as any)?.items || []) as any[]).filter((ev) => !ev.date || ev.date === dateISO).flatMap((ev) =>
        ((ev.roles || []) as any[]).flatMap((role) =>
          ((role.assignees || []) as any[]).map((a) => ({
            id: `${ev.eventId}:${role.id}:${a.staffId}`,
            eventId: String(ev.eventId), roleId: String(role.id), roleLabel: String(role.label || ""),
            eventTitle: String(ev.title || ""), staffId: String(a.staffId), staffName: String(a.staffName || ""),
            fromMin: anyToMin(String(role.window?.start || ev.start || "")),
            toMin: anyToMin(String(role.window?.end || ev.end || "")),
          })))),
      /* a failed or incomplete walk leaves no rooms; the sources strip says why */
      rooms: roomsRes ?? [],
      shifts: (((weekRes as any)?.shifts || []) as WfShift[])
        .filter((sh) => sh.date === dateISO && (sh.staffName || sh.staffId))
        .map((sh) => ({
          staffId: sh.staffId, staffName: sh.staffName || "",
          fromMin: hmToMin(sh.start), toMin: hmToMin(sh.end),
          breaks: (sh.breaks || []).map((br) => ({ fromMin: hmToMin(br.start), toMin: hmToMin(br.end), kind: br.kind })),
        })),
      prices: (((svcRes as any)?.services || (svcRes as any)?.data || []) as any[]).reduce((acc: FloorPriceMap, r: any) => {
        const amt = r?.price?.amount;
        if (typeof amt === "number" && r.name) acc[String(r.name).toLowerCase()] = amt;
        return acc;
      }, {} as FloorPriceMap),
      /* Outlook/Google busy: FIXED time — never draggable, pinned by the external calendar. */
      guests,
      threads: (((inboxRes as any)?.messages || (inboxRes as any)?.data || (inboxRes as any)?.items || []) as any[]).slice(0, 12).map((m: any, i2: number) => ({
        id: String(m.id || "in" + i2),
        name: String(m.customer_name || m.from || m.name || m.sender || "Customer"),
        chan: String(m.channel || "sms").toLowerCase(),
        when: String(m.received_at || m.when || ""),
        unread: !(m.read || m.is_read),
        preview: String(m.preview || m.subject || m.body || "").slice(0, 90),
        context: String(m.context || m.subject || ""),
        msgs: [],
      })),
      externalBusy: (((busyRes as any)?.blocks || []) as any[]).map((b) => ({
        id: String(b.id), label: String(b.label || "Busy"),
        staffId: null, staffName: String(b.staff || ""),
        fromMin: hmToMin(String(b.from || "")), toMin: hmToMin(String(b.to || "")), kind: "external",
        /* raw source rides along — the BOARD names the calendar ("Outlook"/"Google
         * Calendar"); baking it into the label doubled it up. */
        source: String(b.source || "outlook"),
      })) as (FloorBlockRow & { staffName?: string; source?: string })[],
      eventLinking: link.ok ? { ok: true, error: null } : { ok: false, error: link.error },
      sources,
    });
    setLoading(false);
  }, [identity, dateISO, locationId, enabled, scope]);

  useEffect(() => { void load(); }, [load]);
  /* Nothing from another request scope is returned — not even for the render
   * between a scope change and the effect that starts the new read. A caller
   * with no scope (null) sees exactly what it saw before. */
  const scopedDay = !day || (day.requestScope ?? null) === scope;
  const visibleDay = enabled && scopedDay ? day : null;
  const error = enabled && failure && failure.scope === scope ? failure.message : null;
  /* The query identity rides along even while disabled, so a caller can tell
   * which location (and scope) a result, or its absence, belongs to. */
  return {
    day: visibleDay,
    loading: enabled && (loading || !scopedDay),
    error,
    reload: load,
    requestedLocationId: locationId ?? null,
    enabled,
    requestScope: scope,
  };
}

/** True only when `day` is the response to THIS request. useFloorCalendarDay
 * keeps the previous day on screen while a new date or branch loads; posting
 * that day to a board, or resolving a write's row ids against it, would pair
 * old rows with the new date. Pass `requestScope` (the same value given as
 * `opts.scope`) to also require the day's echoed scope; omitted, the scope is
 * not checked, as before. */
export function isFloorDayFor(day: FloorDay | null | undefined, dateISO: string, locationId: string | null = null, requestScope?: string | null): day is FloorDay {
  return !!day && day.dateISO === dateISO && day.requestedLocationId === (locationId ?? null)
    && (requestScope === undefined || (day.requestScope ?? null) === requestScope);
}

/* ── writes (real ops; callers await and reload) ── */
export const moveAppointment = (a: Appointment, toMin: number, staffId: string | null, staffName: string | null) =>
  rescheduleAppointment(a.id, {
    date: a.date,
    start: minToHm(toMin),
    end: minToHm(toMin + (hmToMin(a.end) - hmToMin(a.start))),
    staffId, staffName,
    reason: "Moved on the Floor Calendar",
  });
/** Stage A (2026-08-08, PM-directed exact-UX port): the prototype board consumes
 * its OWN tenant shape — this maps the served day into that exact contract.
 * Statuses map served→prototype (booked/confirmed→ok, checked-in→now,
 * served→done, no-show→noshow); staff are indexed by roster position. */
export function toDCData(day: FloorDay, dayOffset = 0): Record<string, unknown> {
  const staffNames = day.staff.map((st) => st.name);
  const idx = (name: string | null | undefined, id?: string | null): number => {
    if (id != null) { const i = day.staff.findIndex((st) => st.staffId === id); if (i > -1) return i; }
    if (name) { const i = day.staff.findIndex((st) => staffKey(st.name) === staffKey(name)); if (i > -1) return i; }
    return -1;
  };
  const ST: Record<string, string> = { booked: "ok", confirmed: "ok", scheduled: "ok", "checked-in": "now", served: "done", "no-show": "noshow", cancelled: "cancelled" };
  const CH: Record<string, string> = { inPerson: "instore", in_person: "instore", phone: "phone", video: "video", onsite: "onsite" };
  /* stage C wave 1: the board's seeded entry ids are "ap" + index into this
   * array, so the ids ride ALONGSIDE the rows (same filters, same order) and the
   * host resolves "ap{n}" back to the served appointment for real writes. The
   * board's T() copies only its known keys, so bookIds is host-side metadata. */
  const bookRows = day.appointments
    .filter((a) => a.appt.status !== "cancelled")
    .map((a) => ({ id: a.appt.id, appt: a.appt, row: [idx(a.staffName, a.staffId), a.fromMin, a.toMin - a.fromMin, a.appt.customer.name, a.appt.service, ST[a.appt.status] || "ok", CH[a.appt.channel] || "instore"] }))
    .filter((r) => (r.row[0] as number) > -1);
  const book = bookRows.map((r) => r.row);
  /* stage C wave 2: like bookIds — the board's duty ids are "qd" + index into the
   * pushed rows, so the served ids ride alongside in the same order. */
  const dutyRows = day.duty.map((d) => {
    const q = day.queues.find((x) => staffKey(x.name) === staffKey(d.queueName));
    return { id: d.id ?? null, row: [idx(d.staffName), d.fromMin, d.toMin, q ? q.id : d.queueName] };
  }).filter((r) => (r.row[0] as number) > -1);
  const queueDuty = dutyRows.map((r) => r.row);
  /* Board contract: [staffIdx, from, TO, label, source] — the board's external()
   * reader derives dur = x[2] - x[1]. We previously pushed DUR in slot 2, which
   * the board read as an absolute end time: a 12:00–12:45 block became
   * dur = 45 − 720 = −675 min. That one shape mismatch produced the negative
   * utilization headers (−77%/−170%: the booked-minutes sum went negative),
   * broken block geometry, AND silent overlap — a negative-duration block trims
   * nothing and conflicts with nothing, so bookings/duty/events sat on top of
   * externally-busy time (operator-reported, 2026-08-13). x[4] is the raw
   * source ('google'/'outlook') for the board's own calendar labelling. */
  const external = day.externalBusy.map((b) => [idx(b.staffName || null), b.fromMin, b.toMin, b.label, (b as { source?: string }).source || "outlook"]).filter((r) => (r[0] as number) > -1);
  /* Queue rows: [0, joinedMinuteOfDay, elapsedMinutes, ...]. Elapsed comes from
   * absolute instants (Date.now() and the entry's explicit ISO instant), so
   * midnight, DST and the browser's zone never skew it; no explicit instant →
   * 5 min. r[1] stays the legacy browser-local HH:mm seed minute — the board
   * anchors live rows from r[2] and reads r[1] only for finished ones. */
  const nowMs = Date.now();
  const nowDate = new Date(nowMs);
  const nowMin = nowDate.getHours() * 60 + nowDate.getMinutes();
  const queue = day.queues.flatMap((q) =>
    q.entries.map((e) => {
      const joined = new Date(e.joinedAt);
      const jm = isNaN(joined.getTime()) ? nowMin - 5 : joined.getHours() * 60 + joined.getMinutes();
      const joinedMs = explicitInstantMs(e.joinedAt);
      const elapsed = joinedMs == null ? 5 : Math.max(1, Math.floor((nowMs - joinedMs) / 60000));
      return [0, jm, elapsed, e.name, e.service || q.name, "SMS", e.status === "serving" ? "serving" : e.status === "called" ? "called" : "waiting", q.id, false];
    }));
  /* stage B: shifts [staffIdx, from, to, breakFrom, breakTo] — a shiftless
   * roster member gets the board's full window with a zero-length break */
  const shifts = day.staff.map((st, i) => {
    const sh = day.shifts.find((x) => (x.staffId && x.staffId === st.staffId) || staffKey(x.staffName) === staffKey(st.name));
    if (!sh) return [i, DAY_START + 60, DAY_END - 60, DAY_START + 60, DAY_START + 60];
    const br = sh.breaks[0];
    return [i, sh.fromMin, sh.toMin, br ? br.fromMin : sh.fromMin, br ? br.toMin : sh.fromMin];
  });
  /* stage B: events in the prototype's shape. `eventsOn(off)` filters day === off,
   * so they are keyed to the day the board is SHOWING — the host re-reads the
   * served families when the board changes day (floor-day seam, 2026-08-10). */
  /* One event per id: the served families can describe the same event twice (an
   * ops-board row and its studio twin), and the board draws one block per row. */
  const seenEvent = new Set<string>();
  const events = day.events.filter((e) => {
    const k = staffKey(e.title) + ":" + e.fromMin;
    if (seenEvent.has(k)) return false;
    seenEvent.add(k);
    return true;
  }).map((e) => {
    const cover = day.coverage.filter((c) => c.eventId === e.id || staffKey(c.eventTitle) === staffKey(e.title));
    const st = (e.studio || {}) as Record<string, unknown>;
    /* One block per PERSON, not per assignment: coverage lists a row per role, so
     * anyone covering two roles on the same event used to render it twice in their
     * own column (operator-reported, 2026-08-10). */
    const staffIdx = [...new Set(cover.map((c) => idx(c.staffName, c.staffId)).filter((n) => n > -1))];
    return {
      id: e.studioId || e.id, day: dayOffset, title: e.title, from: e.fromMin, to: e.toMin,
      staff: staffIdx,
      /* the event's OWN room when the served row names one (REQ-CAL-4 asks for
       * this field on the contract; the ops-board family carries it today) */
      room: e.resource || day.rooms[2]?.name || day.rooms[0]?.name || "",
      cap: typeof st.capacity === "number" ? st.capacity : e.registered,
      reg: e.registered, waitlist: typeof st.waitlist_count === "number" ? st.waitlist_count : 0,
      owner: cover[0]?.staffName || String(st.staffName || ""),
      cat: "event", desc: String(st.description || ""),
    };
  });
  return {
    staff: day.staff.map((st) => [st.name, st.role || ""]),
    bookIds: bookRows.map((r) => r.id),
    dutyIds: dutyRows.map((r) => r.id),
    book, queueDuty, external, queue, shifts, events,
    guests: day.guests,
    threads: day.threads,
    rooms: day.rooms.map((r) => r.name),
    queues: day.queues.map((q) => ({ id: q.id, name: q.name, resource: null, sla: 15, skills: [] })),
    location: day.locationName || undefined,
    tz: day.tz || undefined,
  };
}

/** Duty drag commit — the engine's move ledger makes this a REAL served write
 *  in the design environment (REQ-CAL-3 tracks the production op). */
export const moveQueueDuty = (id: string, patch: { staff?: string; from?: string; to?: string }) =>
  apiRaw(`/v1/admin/floor/queue-duty/${encodeURIComponent(id)}/move`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) }, "moveQueueDuty");
export { assignEventRole };
export { cancelAppointment, checkInAppointment, markAppointmentNoShow, createCalendarDayBlock, deleteCalendarDayBlock };
